import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { 
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Pagination,
  LinearProgress,
  Checkbox,
  TableSortLabel,
  Divider,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  VoiceOverOff as VoiceOverOffIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { embeddingsApi, getProgressHubUrl } from '../services/api';
import * as signalR from '@microsoft/signalr';
import { Embedding, PaginatedResponse } from '../types/api';

// uploadId is now generated by backend, helper removed

const EmbeddingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [embeddings, setEmbeddings] = useState<Embedding[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [audioUrlsById, setAudioUrlsById] = useState<Record<string, string>>({});
  const [audioLoadingId, setAudioLoadingId] = useState<string | null>(null);
  
  // Add embedding dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [zipDialogOpen, setZipDialogOpen] = useState(false);
  const [selectedZip, setSelectedZip] = useState<File | null>(null);
  const [zipProgress, setZipProgress] = useState<number>(0);
  const [zipResult, setZipResult] = useState<{ added: number; skipped: number; total: number; errors: string[] } | null>(null);
  const [hubConnection, setHubConnection] = useState<signalR.HubConnection | null>(null);
  const [serverProgressStarted, setServerProgressStarted] = useState<boolean>(false);
  const LOCAL_KEY = 'zipUploadActive';
  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);
  const [pageBarProgress, setPageBarProgress] = useState<number>(0);
  const activeUploadIdRef = React.useRef<string | null>(null);
  const uploadAbortRef = React.useRef<AbortController | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  
  // Delete embedding dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [embeddingsToDelete, setEmbeddingsToDelete] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmbeddingId, setEditingEmbeddingId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string>('');
  const [selectedEmbeddings, setSelectedEmbeddings] = useState<Record<string, boolean>>({});
  const [lastDetSort, setLastDetSort] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const searchRef = React.useRef<number | null>(null);

  const loadEmbeddings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: PaginatedResponse<Embedding> = await embeddingsApi.getEmbeddings(
        page,
        pageSize,
        'lastDetection',
        lastDetSort,
        search.trim() || undefined
      );
      setEmbeddings(response.embeddings || []);
      setTotalCount(response.totalCount);
      setSelectedEmbeddings({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить голосовые шаблоны');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmbeddings();
  }, [page, lastDetSort]);

  useEffect(() => {
    if (searchRef.current) {
      window.clearTimeout(searchRef.current);
    }
    searchRef.current = window.setTimeout(() => {
      setPage(1);
      loadEmbeddings();
    }, 300);
    return () => {
      if (searchRef.current) window.clearTimeout(searchRef.current);
    };
  }, [search]);

  useEffect(() => {
    // Restore persisted upload progress
    const saved = localStorage.getItem(LOCAL_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.uploadId) {
          setActiveUploadId(parsed.uploadId);
          activeUploadIdRef.current = parsed.uploadId;
          setPageBarProgress(Number(parsed.progress) || 0);
        }
      } catch {}
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(getProgressHubUrl(), { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    connection.onclose(err => console.log('SignalR closed', err));
    connection.onreconnecting(err => console.log('SignalR reconnecting', err));
    connection.onreconnected(id => console.log('SignalR reconnected', id));

    connection.start()
      .then(async () => {
        console.log('SignalR connected');
        setHubConnection(connection);
        if (activeUploadIdRef.current) {
          await connection.invoke('AddToGroup', activeUploadIdRef.current).catch(err => console.warn('AddToGroup failed', err));
        }
      })
      .catch(err => console.error('SignalR start failed', err));

    connection.on('zipProgress', (payload: any) => {
      if (!payload?.uploadId || payload.uploadId !== activeUploadIdRef.current) return;
      setServerProgressStarted(true);
      if (typeof payload.progress === 'number') {
        setZipProgress(Math.max(payload.progress, 0));
        setPageBarProgress(Math.max(payload.progress, 0));
        try {
          localStorage.setItem(LOCAL_KEY, JSON.stringify({ uploadId: activeUploadIdRef.current, progress: Math.max(payload.progress, 0) }));
        } catch {}
      }
    });

    connection.on('zipCompleted', (payload: any) => {
      if (!payload?.uploadId || payload.uploadId !== activeUploadIdRef.current) return;
      setServerProgressStarted(true);
      setZipResult({ added: payload.added ?? 0, skipped: payload.skipped ?? 0, total: payload.total ?? 0, errors: payload.errors ?? [] });
      setZipProgress(100);
      setPageBarProgress(100);
      try { localStorage.removeItem(LOCAL_KEY); } catch {}
      setActiveUploadId(null);
      activeUploadIdRef.current = null;
      setIsUploading(false);
    });

    connection.on('zipCanceled', (payload: any) => {
      if (!payload?.uploadId || payload.uploadId !== activeUploadIdRef.current) return;
      setServerProgressStarted(false);
      setZipResult(null);
      setPageBarProgress(0);
      setZipProgress(0);
      try { localStorage.removeItem(LOCAL_KEY); } catch {}
      setActiveUploadId(null);
      activeUploadIdRef.current = null;
      setSuccess('Загрузка отменена');
      setIsUploading(false);
    });

    return () => {
      connection.stop();
    };
  }, []);

  // When activeUploadId changes and we have a connection, (re)join its group
  useEffect(() => {
    if (hubConnection && hubConnection.state === signalR.HubConnectionState.Connected && activeUploadId) {
      hubConnection.invoke('AddToGroup', activeUploadId).catch(err => console.warn('AddToGroup failed', err));
    }
  }, [hubConnection, activeUploadId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleAddEmbedding = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      await embeddingsApi.addEmbedding(selectedFile, description || undefined);
      setSuccess('Голосовой шаблон успешно добавлен');
      setSelectedFile(null);
      setDescription('');
      setAddDialogOpen(false);
      loadEmbeddings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить голосовой шаблон');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteEmbeddings = async () => {
    try {
      const result = await embeddingsApi.removeEmbeddings(embeddingsToDelete);
      setSuccess(`Удалено`);
      setEmbeddingsToDelete([]);
      setDeleteDialogOpen(false);
      loadEmbeddings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить голосовые шаблоны');
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const toggleSelectEmbedding = (id: string) => {
    setSelectedEmbeddings(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const allEmbeddingsOnPageSelected = embeddings.length > 0 && embeddings.every(e => selectedEmbeddings[e.id]);
  const someEmbeddingsOnPageSelected = embeddings.some(e => selectedEmbeddings[e.id]);

  const toggleSelectAllEmbeddingsOnPage = () => {
    if (allEmbeddingsOnPageSelected) {
      const copy = { ...selectedEmbeddings };
      embeddings.forEach(e => { delete copy[e.id]; });
      setSelectedEmbeddings(copy);
    } else {
      const copy = { ...selectedEmbeddings };
      embeddings.forEach(e => { copy[e.id] = true; });
      setSelectedEmbeddings(copy);
    }
  };

  const handleLoadAudio = async (embedding: Embedding) => {
    if (!embedding.id && !embedding.fileName) return;
    setAudioLoadingId(embedding.id);
    try {
      let url: string | null = null;
      if (embedding.fileName) {
        url = await embeddingsApi.getFileUrlByFileName(embedding.fileName, 'embeddings');
      } else {
        url = await embeddingsApi.getFileUrlByFileName(embedding.id, 'embeddings');
      }
      if (url) {
        setAudioUrlsById(prev => ({ ...prev, [embedding.id]: url! }));
      }
    } catch (e) {
      setError('Не удалось получить аудио для голосового шаблона');
    } finally {
      setAudioLoadingId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">
          Управление голосовыми шаблонами
        </Typography>
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <TextField
              size="small"
              placeholder="Поиск по описанию"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
              >
                Добавить
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => setZipDialogOpen(true)}
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
              >
                ZIP
              </Button>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={!someEmbeddingsOnPageSelected}
              onClick={() => {
                const toDelete = embeddings.filter(e => selectedEmbeddings[e.id]).map(e => e.id);
                setEmbeddingsToDelete(toDelete);
                setDeleteDialogOpen(true);
              }}
              size="small"
              fullWidth
            >
              Удалить выбранные
            </Button>
          </Box>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Поиск по описанию"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Добавить голосовой шаблон
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setZipDialogOpen(true)}
            >
              Загрузить ZIP
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={!someEmbeddingsOnPageSelected}
              onClick={() => {
                const toDelete = embeddings.filter(e => selectedEmbeddings[e.id]).map(e => e.id);
                setEmbeddingsToDelete(toDelete);
                setDeleteDialogOpen(true);
              }}
            >
              Удалить выбранные
            </Button>
          </>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Persistent ZIP download/import progress bar */}
      {activeUploadId && (serverProgressStarted || isUploading) && pageBarProgress < 100 && (
        <Box sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Импорт ZIP архива</Typography>
            <LinearProgress variant={serverProgressStarted ? 'determinate' : 'indeterminate'} value={serverProgressStarted ? pageBarProgress : undefined} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {serverProgressStarted ? `Прогресс: ${pageBarProgress}%` : 'Ожидание обновлений от сервера...'}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={async () => {
                  const id = activeUploadIdRef.current;
                  try {
                    uploadAbortRef.current?.abort();
                  } catch {}
                  if (id) {
                    try {
                      await embeddingsApi.cancelZip(id);
                    } catch {}
                  }
                  try { localStorage.removeItem(LOCAL_KEY); } catch {}
                  setIsUploading(false);
                  setServerProgressStarted(false);
                  setPageBarProgress(0);
                  setZipProgress(0);
                  setActiveUploadId(null);
                  activeUploadIdRef.current = null;
                  setSuccess('Загрузка отменена');
                }}
              >
                Отмена
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <Card>
        <CardHeader
          avatar={<RecordVoiceOverIcon />}
          title="Голосовые шаблоны"
          subheader={`Всего: ${totalCount} шаблонов`}
        />
        <CardContent>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Checkbox
                  indeterminate={!allEmbeddingsOnPageSelected && someEmbeddingsOnPageSelected}
                  checked={allEmbeddingsOnPageSelected}
                  onChange={toggleSelectAllEmbeddingsOnPage}
                />
                <Typography variant="subtitle2">Выбрать все</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setLastDetSort(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                >
                  Дата детекции: {lastDetSort === 'asc' ? 'Старые' : 'Новые'}
                </Button>
              </Box>
              {embeddings.map((embedding) => (
                <Paper key={embedding.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Checkbox
                      checked={!!selectedEmbeddings[embedding.id]}
                      onChange={() => toggleSelectEmbedding(embedding.id)}
                      size="small"
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', mb: 0.5 }}>
                        {embedding.description || 'Без описания'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {embedding.lastDetection ? (
                          new Date(embedding.lastDetection).toLocaleString()
                        ) : (
                          'Никогда не обнаружено'
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          setEmbeddingsToDelete([embedding.id]);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                          setEditingEmbeddingId(embedding.id);
                          setEditingDescription(embedding.description ?? '');
                          setEditDialogOpen(true);
                        }}
                        aria-label="Редактировать описание"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {audioUrlsById[embedding.id] ? (
                      <audio controls style={{ width: '100%' }} src={audioUrlsById[embedding.id]} />
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleLoadAudio(embedding)}
                        disabled={audioLoadingId === embedding.id}
                        fullWidth
                      >
                        {audioLoadingId === embedding.id ? 'Загрузка...' : 'Показать аудио'}
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={!allEmbeddingsOnPageSelected && someEmbeddingsOnPageSelected}
                        checked={allEmbeddingsOnPageSelected}
                        onChange={toggleSelectAllEmbeddingsOnPage}
                      />
                    </TableCell>
                    <TableCell>Описание</TableCell>
                    <TableCell>Аудио</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {embeddings.map((embedding) => (
                    <TableRow key={embedding.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!selectedEmbeddings[embedding.id]}
                          onChange={() => toggleSelectEmbedding(embedding.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {embedding.description || (
                          <Chip label="Без описания" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {audioUrlsById[embedding.id] ? (
                          <audio controls style={{ width: 220 }} src={audioUrlsById[embedding.id]} />
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleLoadAudio(embedding)}
                            disabled={audioLoadingId === embedding.id}
                          >
                            {audioLoadingId === embedding.id ? 'Загрузка...' : 'Показать аудио'}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setEmbeddingsToDelete([embedding.id]);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          sx={{ ml: 1 }}
                          onClick={() => {
                            setEditingEmbeddingId(embedding.id);
                            setEditingDescription(embedding.description ?? '');
                            setEditDialogOpen(true);
                          }}
                          aria-label="Редактировать описание"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalCount > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: { xs: 1.5, sm: 2 } }}>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Embedding Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить новый голосовой шаблон</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                const file = e.dataTransfer?.files?.[0];
                if (!file) return;
                if (!file.type || !file.type.startsWith('audio')) {
                  setError('Поддерживаются только аудиофайлы');
                  return;
                }
                setSelectedFile(file);
              }}
              sx={{
                mb: 2,
                p: 3,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 1,
                textAlign: 'center',
                bgcolor: isDragging ? 'action.hover' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
              }}
              onClick={() => {
                const inp = document.getElementById('embedding-file-input');
                (inp as HTMLInputElement | null)?.click();
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Перетащите аудиофайл сюда или нажмите для выбора
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Поддерживаются файлы форматов WAV
              </Typography>
              {selectedFile && (
                <Chip sx={{ mt: 1 }} label={`Выбрано: ${selectedFile.name}`} />
              )}
            </Box>
            <input
              accept="audio/*"
              style={{ display: 'none' }}
              id="embedding-file-input"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="embedding-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Выбрать аудиофайл
              </Button>
            </label>
            
            {selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Выбрано: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} МБ)
              </Typography>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Описание (необязательно)"
              fullWidth
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Введите описание для этого голосового шаблона..."
            />

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Обработка аудиофайла и создание эмбеддинга...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Отмена</Button>
          <Button 
            onClick={handleAddEmbedding} 
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            Добавить голосовой шаблон
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Embeddings Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Удалить голосовые шаблоны</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить {embeddingsToDelete.length} шаблон(ов)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteEmbeddings} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Description Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Редактировать описание</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Описание"
            fullWidth
            variant="outlined"
            value={editingDescription}
            onChange={(e) => setEditingDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editingEmbeddingId) return;
              try {
                await embeddingsApi.updateDescription(editingEmbeddingId, editingDescription || undefined);
                setSuccess('Описание обновлено');
                setEditDialogOpen(false);
                setEditingEmbeddingId(null);
                setEditingDescription('');
                loadEmbeddings();
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Не удалось обновить описание');
              }
            }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* ZIP Upload Dialog */}
      <Dialog open={zipDialogOpen} onClose={() => setZipDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Загрузка голосовых шаблонов из ZIP</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              accept=".zip"
              style={{ display: 'none' }}
              id="embedding-zip-input"
              type="file"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setSelectedZip(f);
                setZipResult(null);
                setZipProgress(0);
              }}
            />
            <label htmlFor="embedding-zip-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Выбрать ZIP
              </Button>
            </label>
            {selectedZip && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Выбрано: {selectedZip.name} ({(selectedZip.size / 1024 / 1024).toFixed(2)} МБ)
              </Typography>
            )}

            {zipProgress > 0 && zipProgress < 100 && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant={serverProgressStarted ? 'determinate' : 'indeterminate'} value={serverProgressStarted ? zipProgress : undefined} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {serverProgressStarted ? `Обработка на сервере: ${zipProgress}%` : 'Подключение и подготовка...'}
                </Typography>
              </Box>
            )}

            {zipResult && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Импортировано: {zipResult.added}. Пропущено: {zipResult.skipped}. Всего в архиве: {zipResult.total}.
                {zipResult.errors?.length ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">Ошибки:</Typography>
                    <ul>
                      {zipResult.errors.slice(0, 5).map((e, i) => (
                        <li key={i}>
                          <Typography variant="body2" color="error">{e}</Typography>
                        </li>
                      ))}
                      {zipResult.errors.length > 5 && (
                        <li>
                          <Typography variant="body2" color="text.secondary">...и ещё {zipResult.errors.length - 5}</Typography>
                        </li>
                      )}
                    </ul>
                  </Box>
                ) : null}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZipDialogOpen(false)}>Закрыть</Button>
          <Button
            variant="contained"
            disabled={!selectedZip}
            onClick={async () => {
              if (!selectedZip) return;
              // Start upload; backend returns uploadId
              setPageBarProgress(1);
              setZipProgress(1);
              setServerProgressStarted(false);
              setIsUploading(true);
              try {
                const ac = new AbortController();
                uploadAbortRef.current = ac;
                const { uploadId } = await embeddingsApi.uploadZip(selectedZip, setZipProgress, undefined, ac.signal);
                setActiveUploadId(uploadId);
                activeUploadIdRef.current = uploadId;
                try { localStorage.setItem(LOCAL_KEY, JSON.stringify({ uploadId, progress: 1 })); } catch {}
                if (hubConnection && hubConnection.state === signalR.HubConnectionState.Connected) {
                  try { await hubConnection.invoke('AddToGroup', uploadId); } catch {}
                }
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Не удалось загрузить ZIP');
                setIsUploading(false);
              }
            }}
          >
            Загрузить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmbeddingsPage;
